import { NodeModel, useDragOver } from "@minoru/react-dnd-treeview";
import { ChevronRightIcon, CollectionIcon, EditIcon, GlobeIcon, MenuIcon, TrashIcon } from "@xola/icons";
import { Badge, Tooltip } from "@xola/ui-kit";
import { useHover } from "ahooks";
import { truncate, uniqueId } from "lodash";
import pluralize from "pluralize";
import React, { memo } from "react";
import { CollectionNode, CollectionNodeObject, MAX_COLLECTION_NESTING_DEPTH, ProductObject } from "types";
import { NoTranslate } from "../../../Common/NoTranslate";
import { CollectionImage } from "./CollectionImage";
import { DeleteCollectionModal } from "./DeleteCollectionModal";
import { EditCollectionModal } from "./EditCollectionModal";
import { getProductObjectIcon } from "@/components/Common/ProductObjectFilter";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useProduct } from "@/hooks/resources/useProduct";
import { useCollection } from "@/hooks/useCollection";
import { useModal } from "@/hooks/useVisibility";
import { getCollectionNestingDepth, getConstructedItemPath } from "@/models/CollectionNode";

interface CollectionItemProps {
    node: NodeModel<CollectionNode> & {
        nestedCollections?: string[];
        nestedProducts?: string[];
        nestedProductIds?: string[];
    };
    depth: number;
    isOpen?: boolean;
    handleRef?: React.RefObject<HTMLDivElement | null>;
    onToggle?: (id: string | number) => void;
    showProductItem?: boolean; // to show product collection items in the collection tree
    isDragging?: boolean; // disable memo during drag operations
}

export const CollectionItem = memo(
    ({ node, depth, isOpen, handleRef, onToggle, showProductItem = false }: CollectionItemProps) => {
        const { data: collection } = node;
        const deleteModal = useModal();
        const editModal = useModal();

        // Fetch product details if this is a product collection item
        const isProductItem = collection?.object === CollectionNodeObject.PRODUCT_COLLECTION_NODE;
        const { product } = useProduct(collection?.product?.id, null, {
            enabled: isProductItem && showProductItem && !!collection?.product?.id,
        });

        // Calculate if collection is at max depth when showProductItem is false
        const collectionPath = collection?.path ?? "";
        const currentDepth = getCollectionNestingDepth(collectionPath);
        const isCollectionNodeAtMaxDepth = currentDepth >= MAX_COLLECTION_NESTING_DEPTH && !showProductItem;

        // Only show caret and allow dropping if not at max depth
        const isDroppable = node.droppable && !isCollectionNodeAtMaxDepth;
        const dragOverProps = useDragOver(node.id, isOpen ?? false, onToggle ?? (() => undefined));

        // Don't render if it's a product item and showProductItem is false
        if (isProductItem && !showProductItem) {
            return null;
        }

        if (!collection) {
            return null;
        }

        const handleToggle = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (onToggle) {
                onToggle(node.id);
            }
        };

        const isDraggable = collection.object === CollectionNodeObject.GROUP_COLLECTION_NODE;

        return (
            <>
                <div
                    className="flex items-center justify-between gap-2 py-1 px-3"
                    style={{ paddingLeft: `${depth * 40 + 12}px` }}
                    {...dragOverProps}
                >
                    <div className="flex flex-1 items-center gap-2">
                        {/* Drag Handle - only show for GROUP_COLLECTION_NODE */}
                        {isDraggable ? (
                            <div
                                ref={handleRef as React.LegacyRef<HTMLDivElement>}
                                className="flex cursor-grab items-center justify-center"
                            >
                                <MenuIcon size="small" className="text-gray-dark" />
                            </div>
                        ) : (
                            <div className="flex w-4 items-center justify-center" />
                        )}

                        {/* Expand/Collapse Caret */}
                        <div className="flex w-4 items-center justify-center">
                            {isDroppable ? (
                                <button
                                    type="button"
                                    className="flex h-4 w-4 items-center justify-center rounded hover:bg-gray-lighter"
                                    onClick={handleToggle}
                                >
                                    <ChevronRightIcon
                                        size="small"
                                        className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                                    />
                                </button>
                            ) : (
                                <div className="h-4 w-4" />
                            )}
                        </div>

                        <CollectionImage collection={collection} size="small" />

                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                                <CollectionItemIcon itemType={collection.object} productType={product?.object} />
                                <Tooltip
                                    content={collection.name ?? ""}
                                    disabled={(collection.name?.length ?? 0) <= 100}
                                >
                                    <NoTranslate className="text-sm font-semibold leading-tight line-clamp-2">
                                        {collection.name}
                                    </NoTranslate>
                                </Tooltip>
                            </div>

                            {/* Badges below the collection name */}
                            <div className="flex items-center gap-2">
                                {collection.object === CollectionNodeObject.GROUP_COLLECTION_NODE && (
                                    <>
                                        {isDroppable && (
                                            <CountBadge
                                                object="Collection"
                                                names={node.nestedCollections}
                                                collection={collection}
                                            />
                                        )}
                                        <CountBadge
                                            object="Product"
                                            collectionId={collection.id}
                                            collectionPath={collection.path}
                                            collection={collection}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="actions flex items-center gap-1.5">
                        <SecondaryButton size="small" onClick={editModal.show}>
                            <EditIcon />
                        </SecondaryButton>
                        <SecondaryButton size="small" onClick={deleteModal.show}>
                            <TrashIcon />
                        </SecondaryButton>
                    </div>
                </div>

                <DeleteCollectionModal
                    collection={collection}
                    isOpen={deleteModal.isVisible}
                    onClose={deleteModal.hide}
                />

                <EditCollectionModal collection={collection} isOpen={editModal.isVisible} onClose={editModal.hide} />
            </>
        );
    },
    (previous, next) => {
        // Only memoize during drag operations for performance
        // When NOT dragging, always re-render to ensure expand/collapse works
        if (!previous.isDragging && !next.isDragging) {
            return false;
        }

        // During drag, check all relevant props for changes
        const previousData = previous.node.data;
        const nextData = next.node.data;
        const previousCounts = previousData?.childrenCount;
        const nextCounts = nextData?.childrenCount;

        return (
            previous.node.id === next.node.id &&
            previousData?.sequence === nextData?.sequence &&
            previousData?.path === nextData?.path &&
            previousData?.name === nextData?.name &&
            previousData?.photo?.id === nextData?.photo?.id &&
            previousCounts?.product_collection_node === nextCounts?.product_collection_node &&
            previousCounts?.catalog_item_collection_node === nextCounts?.catalog_item_collection_node &&
            previous.depth === next.depth &&
            previous.isOpen === next.isOpen &&
            previous.showProductItem === next.showProductItem
        );
    },
);

interface CollectionItemIconProps {
    itemType: CollectionNodeObject;
    productType?: ProductObject;
}

const CollectionItemIcon = React.memo(({ itemType, productType }: CollectionItemIconProps) => {
    if (itemType === CollectionNodeObject.GROUP_COLLECTION_NODE) {
        return <CollectionIcon size="small" />;
    }

    if (productType) {
        return getProductObjectIcon(productType);
    }

    return <GlobeIcon size="small" />;
});

interface CountBadgeProps {
    object: "Collection" | "Product";
    names?: string[];
    collectionId?: string;
    collectionPath?: string;
    collection?: CollectionNode;
}

const CountBadge = React.memo(({ object, names = [], collectionId, collectionPath, collection }: CountBadgeProps) => {
    const hoverRef = React.useRef(null);
    const isHovered = useHover(hoverRef);
    const isProductObject = object === "Product";

    // Get count from childrenCount if available
    const count = React.useMemo(() => {
        if (!collection?.childrenCount) return 0;
        if (isProductObject) {
            return (
                collection.childrenCount.product_collection_node + collection.childrenCount.catalog_item_collection_node
            );
        }
        return names.length;
    }, [collection?.childrenCount, isProductObject, names.length]);

    // count == 0 in case for Product badge, names length == 0 for Collection badge
    const isTooltipDisabled = count === 0 || (!isProductObject && names.length === 0) || !isHovered;

    return (
        <div ref={hoverRef}>
            <Tooltip
                content={
                    <TooltipContent
                        isProductObject={isProductObject}
                        names={names}
                        count={count}
                        collectionId={collectionId}
                        collectionPath={collectionPath}
                        isHovered={isHovered}
                    />
                }
                disabled={isTooltipDisabled}
            >
                <Badge size="small" color="secondary">
                    <div className="text-xs">
                        <var>{count} </var>
                        {pluralize(object, count ?? 0)}
                    </div>
                </Badge>
            </Tooltip>
        </div>
    );
});

interface TooltipContentProps {
    isProductObject: boolean;
    names: string[];
    count: number;
    collectionId?: string;
    collectionPath?: string;
    isHovered: boolean;
}

const TooltipContent = ({
    isProductObject,
    names,
    count,
    collectionId,
    collectionPath,
    isHovered,
}: TooltipContentProps) => {
    // Fetch Collection Product items only when hovering on product badge
    const collectionItemPath = collectionId ? getConstructedItemPath(collectionPath ?? "", collectionId) : undefined;
    const { data: productCollectionNode = [] } = useCollection<CollectionNode>(
        "collectionNodes",
        { path: collectionItemPath, limit: 10 },
        { enabled: isProductObject && isHovered && !!collectionId && count > 0 },
    );

    // Extract product IDs from children
    const productIds = React.useMemo(() => {
        if (!isProductObject) {
            return [];
        }

        const extractedProductIds = productCollectionNode
            .filter((child: CollectionNode) => {
                const isProductOrCatalogNode =
                    child.object === CollectionNodeObject.PRODUCT_COLLECTION_NODE ||
                    child.object === CollectionNodeObject.CATALOG_ITEM_COLLECTION_NODE;
                const hasProductId = !!child.product?.id;
                return isProductOrCatalogNode && hasProductId;
            })
            .map((child: CollectionNode) => child.product!.id);

        return extractedProductIds;
    }, [productCollectionNode, isProductObject]);

    if (isProductObject) {
        return (
            <ul className="list-disc space-y-1 rounded-lg py-1 pl-3 pr-2 text-xs">
                {productIds.length > 0 ? (
                    <>
                        {productIds.map((productId: string) => (
                            <ProductListItem key={productId} productId={productId} />
                        ))}
                        {count > 10 && <li className="italic">and {count - 10} more...</li>}
                    </>
                ) : (
                    <li>Loading...</li>
                )}
            </ul>
        );
    }

    return (
        <ul className="list-disc space-y-1 rounded-lg py-1 pl-3 pr-2 text-xs">
            {names.map((name) => (
                <li key={uniqueId(`collection-${name}`)}>{name}</li>
            ))}
        </ul>
    );
};

interface ProductListItemProps {
    productId: string;
}

const ProductListItem = ({ productId }: ProductListItemProps) => {
    const { data: product } = useProduct(productId);

    if (!product) {
        return <li className="flex items-center gap-1.5">Loading...</li>;
    }

    return (
        <li className="flex items-center gap-1.5">
            {getProductObjectIcon(product.object)}
            <span>{truncate(product.name, { length: 65 })}</span>
        </li>
    );
};
